import { jsPDF } from "jspdf";

const FR_DAYS = { lundi: 0, mardi: 1, mercredi: 2, jeudi: 3, vendredi: 4, samedi: 5, dimanche: 6 };
const TYPE_LABEL = { running: "Course", strength: "Force", hyrox_wod: "WOD HYROX" };

function mondayOf(dateStr) {
  const d = dateStr ? new Date(dateStr) : new Date();
  const day = (d.getDay() + 6) % 7; // Monday = 0
  const m = new Date(d);
  m.setDate(d.getDate() - day);
  m.setHours(0, 0, 0, 0);
  return m;
}

function parseT(t) {
  const [h, m] = (t || "").split(":").map(Number);
  return isNaN(h) ? null : { h, m: m || 0 };
}

function sessionDate(program, week, session) {
  const base = mondayOf(program?.created_at);
  const wk = (week?.week_number || 1) - 1;
  const di = FR_DAYS[(session?.day || "").trim().toLowerCase()] ?? 0;
  const d = new Date(base);
  d.setDate(base.getDate() + wk * 7 + di);
  const slot = (session?.time_slot || "").replace(/[–—]/g, "-");
  const [start, end] = slot.split("-").map((s) => s.trim());
  const st = parseT(start) || { h: 18, m: 0 };
  const et = parseT(end) || { h: st.h + 1, m: st.m };
  const startD = new Date(d);
  startD.setHours(st.h, st.m, 0, 0);
  const endD = new Date(d);
  endD.setHours(et.h, et.m, 0, 0);
  if (endD <= startD) endD.setTime(startD.getTime() + 60 * 60000);
  return { startD, endD };
}

function fmtICS(d) {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}T${p(d.getHours())}${p(d.getMinutes())}00`;
}

export function sessionStart(program, week, session) {
  return sessionDate(program, week, session).startD;
}

function sessionDescription(s) {
  const parts = [];
  if (s.warmup) parts.push(`Échauffement: ${s.warmup}`);
  if (s.main?.length) parts.push(`Corps: ${s.main.join(" | ")}`);
  if (s.finisher) parts.push(`Finisher: ${s.finisher}`);
  if (s.recovery) parts.push(`Récup: ${s.recovery}`);
  return parts.join("\n");
}

export function googleCalendarUrl(program, week, session) {  const { startD, endD } = sessionDate(program, week, session);
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: `HYROX — ${session.title || "Séance"}`,
    dates: `${fmtICS(startD)}/${fmtICS(endD)}`,
    details: sessionDescription(session),
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export function downloadSessionIcs(program, week, session) {
  const { startD, endD } = sessionDate(program, week, session);
  const uid = `${(session.id || Math.random()).toString()}@roxlab`;
  const esc = (t) => String(t || "").replace(/([,;\\])/g, "\\$1").replace(/\n/g, "\\n");
  const ics = [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Roxlab//FR", "CALSCALE:GREGORIAN", "BEGIN:VEVENT",
    `UID:${uid}`, `DTSTAMP:${fmtICS(new Date())}`,
    `DTSTART:${fmtICS(startD)}`, `DTEND:${fmtICS(endD)}`,
    `SUMMARY:${esc("HYROX — " + (session.title || "Séance"))}`,
    `DESCRIPTION:${esc(sessionDescription(session))}`,
    "END:VEVENT", "END:VCALENDAR",
  ].join("\r\n");
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `hyrox-${(session.title || "seance").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}.ics`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function downloadWeekPdf(program, week) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const M = 40;
  let y = 90;
  const W = doc.internal.pageSize.getWidth();
  const line = (txt, size, color, gap = 14, bold = false) => {
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(size);
    doc.setTextColor(color?.[0] ?? 30, color?.[1] ?? 30, color?.[2] ?? 30);
    const lines = doc.splitTextToSize(txt, W - 2 * M);
    lines.forEach((l) => {
      if (y > 790) { doc.addPage(); y = M; }
      doc.text(l, M, y);
      y += gap;
    });
  };
  doc.setFillColor(10, 10, 10);
  doc.rect(0, 0, W, 60, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text("ROXLAB", M, 38);
  doc.setTextColor(255, 59, 48);
  doc.setFontSize(10);
  doc.text("Coaching sportif & nutritionnel HYROX", M, 52);
  line(program?.name || "Programme HYROX", 16, [10, 10, 10], 20, true);
  line(`Semaine ${week.week_number} · ${week.phase || ""}`, 12, [255, 59, 48], 18, true);
  if (week.focus) line(week.focus, 10, [90, 90, 90], 16);
  y += 6;
  (week.sessions || []).forEach((s, i) => {
    if (y > 740) { doc.addPage(); y = M; }
    doc.setDrawColor(220, 220, 220);
    doc.line(M, y, W - M, y);
    y += 16;
    line(`${s.day || ""} · ${s.time_slot || ""} · ${TYPE_LABEL[s.type] || s.type || ""}`, 9, [130, 130, 130], 14);
    line(s.title || `Séance ${i + 1}`, 13, [10, 10, 10], 18, true);
    if (s.warmup) line(`Échauffement : ${s.warmup}`, 10, [60, 60, 60]);
    if (s.main?.length) {
      line("Corps de séance :", 10, [60, 60, 60], 14, true);
      s.main.forEach((m) => line(`•  ${m}`, 10, [60, 60, 60], 14));
    }
    if (s.finisher) line(`Finisher : ${s.finisher}`, 10, [255, 59, 48]);
    if (s.recovery) line(`Récup : ${s.recovery}`, 10, [60, 60, 60]);
    y += 6;
  });
  doc.save(`hyrox-semaine-${week.week_number}.pdf`);
}
